// Static content for the marketing homepage, transcribed from the Claude Design handoff.
// These are placeholder/demo values (the design is a mock); real event data is out of scope
// for the landing page and would be wired through the events schema separately.
import type { ComponentType, SVGProps } from "react";
import {
  IconAnalytics,
  IconCaptureLeads,
  IconCheckIn,
  IconCommunity,
  IconRegister,
  IconVendors,
} from "./components/icons.tsx";

// Public-path assets (served from /public).
export const assets = {
  wordmark: "/restomag-wordmark.png",
  logo: "/restomag-logo.png",
  eyekandy: "/hero-carshow.jpeg",
  hero: "/eyekandy-openhouse.jpeg",
  forOrganizers: "/for-organizers.jpg",
} as const;

// Slug of the currently featured event — resolves to its public event page via the `/:slug` route.
// Shared by the Hero "Up next" card and the Featured section so they point to the same place.
export const FEATURED_EVENT_SLUG = "eyekandy-openhouse-2026";

// "Host an Event" CTAs route to the /host marketing page (the organizer dashboard/signup isn't
// built yet); that page offers a pre-addressed email as the contact action for the pilot.
export const HOST_ROUTE = "/host";
export const HOST_EMAIL = "events@restomag.com";
export const MAILTO_HOST = `mailto:${HOST_EMAIL}?subject=${encodeURIComponent(
  "Hosting an event with RestoMag",
)}`;

export const FLAME = "#dd2a02";
export const SEA = "#2f7fb5";
export const AIR = "#7d8a96";

// Sections hidden for the initial launch — flip one to `true` to bring it back
// (controls the homepage section, plus its nav and footer links, from one place).
export const launchFlags = {
  landSeaAir: false,
  discover: false,
  community: false,
} as const;


export type Theatre = {
  index: string;
  kicker: string;
  title: string;
  body: string;
  tags: string[];
  cta: string;
  placeholder: string;
};

export const theatres: Theatre[] = [
  {
    index: "01",
    kicker: "The road",
    title: "Land",
    body: "Cars, trucks and motorsport. Pavement still warm at sunrise, builds rolled out of the shop, and the unmistakable sound of an engine finding its line.",
    tags: ["Car Shows", "Cars & Coffee", "Track Days", "Cruise Nights"],
    cta: "Explore Land events",
    placeholder: "LAND — Mustang at a show, Cars & Coffee, track day",
  },
  {
    index: "02",
    kicker: "Open water",
    title: "Sea",
    body: "Marine restoration and offshore performance. Polished mahogany on glassy harbours, twin engines barking across the bay, and salt in the air.",
    tags: ["Boat Shows", "Marina Events", "Offshore Runs", "Wooden Boats"],
    cta: "Explore Sea events",
    placeholder: "SEA — offshore performance boat, wooden boat show, marina",
  },
  {
    index: "03",
    kicker: "The runway",
    title: "Air",
    body: "Aviation restoration and the warbirds that earned their scars. Radial engines turning over at dawn, formation passes, and history you can hear before you see.",
    tags: ["Airshows", "Fly-Ins", "Warbirds", "Vintage Aircraft"],
    cta: "Explore Air events",
    placeholder: "AIR — warbird flyby, vintage aircraft on a runway, fly-in",
  },
];

export type Category = "land" | "sea" | "air";

export const categoryLabel: Record<Category, string> = { land: "Land", sea: "Sea", air: "Air" };
export const categoryColor: Record<Category, string> = { land: FLAME, sea: SEA, air: AIR };

export type EventItem = {
  name: string;
  org: string;
  date: string;
  place: string;
  cat: Category;
  tags: string[];
};

export const events: EventItem[] = [
  { name: "Pacific Cars & Coffee — Sunrise Session", org: "Westside Auto Collective", date: "Sat · Jun 28", place: "Vancouver, BC", cat: "land", tags: ["weekend", "free"] },
  { name: "High Desert Warbird Fly-In", org: "Mojave Heritage Air", date: "Jul 12–13", place: "Mojave, CA", cat: "air", tags: ["featured"] },
  { name: "Offshore Performance Showcase", org: "Pacific Marine Club", date: "Sat · Jul 19", place: "San Diego, CA", cat: "sea", tags: ["weekend", "featured"] },
  { name: "Sea-to-Sky Track Day", org: "Apex Drivers Group", date: "Sat · Jul 5", place: "Pemberton, BC", cat: "land", tags: ["weekend"] },
  { name: "Wooden Boat & Marina Classic", org: "Victoria Maritime Society", date: "Aug 2–3", place: "Victoria, BC", cat: "sea", tags: ["free"] },
  { name: "Friday Night Cruise & Lights", org: "Lower Mainland Cruisers", date: "Fri · Jun 27", place: "Surrey, BC", cat: "land", tags: ["free"] },
];

export const filterChips = [
  { key: "all", label: "All" },
  { key: "land", label: "Land" },
  { key: "sea", label: "Sea" },
  { key: "air", label: "Air" },
  { key: "weekend", label: "This Weekend" },
  { key: "free", label: "Free" },
  { key: "featured", label: "Featured" },
] as const;

export type FilterKey = (typeof filterChips)[number]["key"];

// Faux map pins (percentage positions inside the map panel).
export type MapPin = {
  label: string;
  left: string;
  top: string;
  primary?: boolean;
  color?: string;
};

export const mapPins: MapPin[] = [
  { label: "Vancouver · 38", left: "18%", top: "30%", primary: true },
  { label: "San Diego · 21", left: "22%", top: "58%" },
  { label: "Mojave · 12", left: "30%", top: "50%" },
  { label: "Austin · 44", left: "54%", top: "42%" },
  { label: "Detroit · 31", left: "78%", top: "34%" },
  { label: "Miami · 27", left: "85%", top: "54%" },
  { label: "Chicago · 33", left: "62%", top: "24%", color: SEA },
];

// Featured supporting cards (the EyeKandy hero feature is rendered explicitly).
export const supportingFeatures: { date: string; title: string; org: string; cat: Category; placeholder: string }[] = [
  { date: "Jul 12–13", title: "High Desert Warbird Fly-In", org: "Mojave Heritage Air · Mojave, CA", cat: "air", placeholder: "Warbird fly-in" },
  { date: "Sat · Jul 19", title: "Offshore Performance Showcase", org: "Pacific Marine Club · San Diego, CA", cat: "sea", placeholder: "Offshore boat showcase" },
  { date: "Sat · Jul 5", title: "Sea-to-Sky Track Day", org: "Apex Drivers Group · Pemberton, BC", cat: "land", placeholder: "Track day action" },
];

// Community mosaic tiles (grid placement classes from the design).
export type CommunityTile = {
  area: string; // tailwind grid-area shorthand via col/row classes
  kicker?: string;
  caption: string;
  placeholder: string;
};

// `area` placement is gated to `md:` so the bento only applies on tablet+; on mobile the
// tiles flow in a simple 2-column grid (the first tile spans both columns as a feature).
export const communityTiles: CommunityTile[] = [
  { area: "col-span-2 md:col-start-1 md:col-end-3 md:row-start-1 md:row-end-3", kicker: "The reveal", caption: "Doors up, Vancouver", placeholder: "Build reveal — crowd around a fresh car" },
  { area: "md:col-start-3 md:col-end-4 md:row-start-1 md:row-end-2", caption: "First show together", placeholder: "Father & son at first show" },
  { area: "md:col-start-4 md:col-end-5 md:row-start-1 md:row-end-2", caption: "Best in Show", placeholder: "Best in Show trophy" },
  { area: "md:col-start-3 md:col-end-5 md:row-start-2 md:row-end-3", kicker: "After dark", caption: "Friday night cruise", placeholder: "Night cruise / city lights" },
  { area: "md:col-start-1 md:col-end-2 md:row-start-3 md:row-end-4", caption: "Engine start", placeholder: "Warbird taxi" },
  { area: "md:col-start-2 md:col-end-4 md:row-start-3 md:row-end-4", kicker: "Glass water", caption: "Harbour at dawn", placeholder: "Marina morning / wooden boats" },
  { area: "md:col-start-4 md:col-end-5 md:row-start-3 md:row-end-4", caption: "Smoke show", placeholder: "Burnout / tire smoke" },
];

// RestoMag showcase carousel — build-story arc sourced from the RestoMag site gallery
// (finished → engine install → bare metal). Swap for updated photos in /public anytime.
export type RestoMagSlide = { img: string; line: string };
export const restomagSlides: RestoMagSlide[] = [
  { img: "/restomag-build-1.jpg", line: "Your Build, Your Story. Verified." },
  { img: "/restomag-build-2.jpg", line: "Where every restoration gets its story." },
  { img: "/restomag-build-3.jpg", line: "Built by enthusiasts. Backed by RestoMag." },
];

export const marqueeWords = [
  "Cars",
  "Trucks",
  "Motorsport",
  "Aircraft",
  "Boats",
  "Builders",
  "Collectors",
  "Restoration",
];

export type HostBenefit = {
  icon: ComponentType<SVGProps<SVGSVGElement> & { size?: number }>;
  title: string;
  body: string;
};

export const hostBenefits: HostBenefit[] = [
  { icon: IconCaptureLeads, title: "Capture Leads", body: "Turn every walk-up into a contact you keep." },
  { icon: IconRegister, title: "Register Attendees", body: "Free or paid tickets, RSVPs and waitlists." },
  { icon: IconCheckIn, title: "Run Check-In", body: "Scan, wave through, no lineup at the gate." },
  { icon: IconVendors, title: "Manage Vendors", body: "Booths, food trucks and sponsors in one place." },
  { icon: IconAnalytics, title: "Track Analytics", body: "See attendance, sources and revenue live." },
  { icon: IconCommunity, title: "Grow Communities", body: "Bring people back, season after season." },
];

export const footerColumns: { heading: string; links: { href: string; label: string }[] }[] = [
  {
    heading: "Discover",
    links: [
      { href: "#featured", label: "Featured Events" },
      ...(launchFlags.discover ? [{ href: "#map", label: "Events Near You" }] : []),
      ...(launchFlags.landSeaAir ? [{ href: "#lsa", label: "Land · Sea · Air" }] : []),
      ...(launchFlags.community ? [{ href: "#community", label: "Community" }] : []),
    ],
  },
  {
    heading: "Organizers",
    links: [
      { href: HOST_ROUTE, label: "Host an Event" },
      { href: HOST_ROUTE, label: "Capture Leads" },
      { href: HOST_ROUTE, label: "Run Check-In" },
    ],
  },
];
